import { useEffect, useState, useCallback } from "react";
import {
  listDrivers, createDriver, updateDriver,
  listPartners, createPartner, updatePartner,
  listClients, suspendClient, reactivateClient,
} from "@/lib/rootConsole";

const card = { background: "#121924", border: "1px solid #22303f", borderRadius: 10, padding: 16, marginBottom: 16 };
const btn = { background: "#1f6feb", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", cursor: "pointer", marginRight: 8 };
const input = { background: "#0b0f14", color: "#d7e0ea", border: "1px solid #22303f", borderRadius: 8, padding: 10, width: "100%", marginTop: 8, boxSizing: "border-box" };
const tabBtn = (active) => ({ ...btn, background: active ? "#1f6feb" : "#30363d" });
const th = { textAlign: "left", color: "#8b98a5", fontSize: 12, padding: "6px 8px", borderBottom: "1px solid #22303f" };
const td = { color: "#d7e0ea", fontSize: 13, padding: "6px 8px", borderBottom: "1px solid #1a2430" };

const EMPTY_DRIVER = { name: "", email: "", phone: "", iban: "" };
const EMPTY_PARTNER = { name: "", email: "", phone: "", address: "", iban: "", shopify_collection: "" };

// GESTION — poste de pilotage du Founder (Tuluss) : Livreurs & Partenaires.
export default function GestionPanel() {
  const [tab, setTab] = useState("livreurs");
  const [drivers, setDrivers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientQ, setClientQ] = useState("");
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [dForm, setDForm] = useState(EMPTY_DRIVER);
  const [pForm, setPForm] = useState(EMPTY_PARTNER);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(null), 3000); };

  const load = useCallback(async () => {
    setErr(null);
    try {
      const [d, p] = await Promise.all([listDrivers(), listPartners()]);
      setDrivers(d || []);
      setPartners(p || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Accès refusé — connecte-toi en Founder (/login) avec le compte admin.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadClients = useCallback(async (q = "") => {
    try { setClients(await listClients(q)); } catch (e) { setErr(e?.response?.data?.detail || String(e)); }
  }, []);
  useEffect(() => { if (tab === "clients") loadClients(clientQ); }, [tab, clientQ, loadClients]);

  const toggleClient = async (c) => {
    try { c.is_active ? await suspendClient(c.id) : await reactivateClient(c.id); flash("Client mis à jour."); loadClients(clientQ); }
    catch (e) { setErr(e?.response?.data?.detail || String(e)); }
  };

  const submitDriver = async () => {
    if (!dForm.name || !dForm.email || !dForm.phone) { setErr("Nom, email et téléphone requis."); return; }
    try {
      await createDriver(dForm);
      setDForm(EMPTY_DRIVER);
      flash("Livreur créé.");
      load();
    } catch (e) { setErr(e?.response?.data?.detail || String(e)); }
  };
  const toggleDriver = async (d) => {
    try { await updateDriver(d.id, { is_active: !d.is_active }); flash("Livreur mis à jour."); load(); }
    catch (e) { setErr(e?.response?.data?.detail || String(e)); }
  };

  const submitPartner = async () => {
    if (!pForm.name || !pForm.email || !pForm.phone || !pForm.address) { setErr("Nom, email, téléphone et adresse requis."); return; }
    try {
      await createPartner(pForm);
      setPForm(EMPTY_PARTNER);
      flash("Partenaire créé.");
      load();
    } catch (e) { setErr(e?.response?.data?.detail || String(e)); }
  };
  const togglePartner = async (p) => {
    try { await updatePartner(p.id, { is_active: !p.is_active }); flash("Partenaire mis à jour."); load(); }
    catch (e) { setErr(e?.response?.data?.detail || String(e)); }
  };

  return (
    <div style={card} data-testid="gestion-panel">
      <h3 style={{ color: "#58a6ff", marginTop: 0 }}>GESTION · Poste de pilotage</h3>
      <div style={{ color: "#8b98a5", fontSize: 13, marginBottom: 12 }}>
        Pilote les opérations de La BASE : livreurs, partenaires. (Espaces clients &amp; mises en page : phases suivantes.)
      </div>

      <div style={{ marginBottom: 14 }}>
        <button style={tabBtn(tab === "livreurs")} onClick={() => setTab("livreurs")} data-testid="gestion-tab-livreurs">🛵 Livreurs ({drivers.length})</button>
        <button style={tabBtn(tab === "partenaires")} onClick={() => setTab("partenaires")} data-testid="gestion-tab-partenaires">🤝 Partenaires ({partners.length})</button>
        <button style={tabBtn(tab === "clients")} onClick={() => setTab("clients")} data-testid="gestion-tab-clients">👤 Clients</button>
        <button style={{ ...btn, background: "#30363d" }} onClick={load} data-testid="gestion-refresh">↻ Rafraîchir</button>
      </div>

      {err && <div style={{ ...card, borderColor: "#f85149", color: "#ff7b72" }} data-testid="gestion-error">{err}</div>}
      {msg && <div style={{ color: "#7ee787", fontSize: 13, marginBottom: 10 }} data-testid="gestion-msg">{msg}</div>}

      {tab === "livreurs" && (
        <div data-testid="gestion-livreurs">
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
            <thead><tr><th style={th}>Nom</th><th style={th}>Email</th><th style={th}>Téléphone</th><th style={th}>Livraisons</th><th style={th}>État</th><th style={th}></th></tr></thead>
            <tbody>
              {drivers.length === 0 && <tr><td style={td} colSpan={6}>Aucun livreur.</td></tr>}
              {drivers.map((d) => (
                <tr key={d.id} data-testid={`driver-row-${d.id}`}>
                  <td style={td}>{d.name}</td>
                  <td style={td}>{d.email}</td>
                  <td style={td}>{d.phone}</td>
                  <td style={td}>{d.total_deliveries ?? 0}</td>
                  <td style={{ ...td, color: d.is_active ? "#7ee787" : "#ff7b72" }}>{d.is_active ? "Actif" : "Inactif"}</td>
                  <td style={td}>
                    <button style={{ ...btn, padding: "4px 10px", background: d.is_active ? "#da3633" : "#238636", marginRight: 0 }} onClick={() => toggleDriver(d)} data-testid={`driver-toggle-${d.id}`}>
                      {d.is_active ? "Désactiver" : "Activer"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ borderTop: "1px solid #22303f", paddingTop: 12 }}>
            <div style={{ color: "#e3b341", marginBottom: 6 }}>Ajouter un livreur</div>
            <input style={input} placeholder="Nom complet" value={dForm.name} onChange={(e) => setDForm({ ...dForm, name: e.target.value })} data-testid="driver-input-name" />
            <input style={input} placeholder="Email" value={dForm.email} onChange={(e) => setDForm({ ...dForm, email: e.target.value })} data-testid="driver-input-email" />
            <input style={input} placeholder="Téléphone" value={dForm.phone} onChange={(e) => setDForm({ ...dForm, phone: e.target.value })} data-testid="driver-input-phone" />
            <input style={input} placeholder="IBAN (optionnel)" value={dForm.iban} onChange={(e) => setDForm({ ...dForm, iban: e.target.value })} data-testid="driver-input-iban" />
            <button style={{ ...btn, marginTop: 10 }} onClick={submitDriver} data-testid="driver-submit">Créer le livreur</button>
          </div>
        </div>
      )}

      {tab === "partenaires" && (
        <div data-testid="gestion-partenaires">
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
            <thead><tr><th style={th}>Nom</th><th style={th}>Email</th><th style={th}>Adresse</th><th style={th}>Collection</th><th style={th}>État</th><th style={th}></th></tr></thead>
            <tbody>
              {partners.length === 0 && <tr><td style={td} colSpan={6}>Aucun partenaire.</td></tr>}
              {partners.map((p) => (
                <tr key={p.id} data-testid={`partner-row-${p.id}`}>
                  <td style={td}>{p.name}</td>
                  <td style={td}>{p.email}</td>
                  <td style={td}>{p.address}</td>
                  <td style={td}>{p.shopify_collection || "—"}</td>
                  <td style={{ ...td, color: p.is_active ? "#7ee787" : "#ff7b72" }}>{p.is_active ? "Actif" : "Inactif"}</td>
                  <td style={td}>
                    <button style={{ ...btn, padding: "4px 10px", background: p.is_active ? "#da3633" : "#238636", marginRight: 0 }} onClick={() => togglePartner(p)} data-testid={`partner-toggle-${p.id}`}>
                      {p.is_active ? "Désactiver" : "Activer"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ borderTop: "1px solid #22303f", paddingTop: 12 }}>
            <div style={{ color: "#e3b341", marginBottom: 6 }}>Ajouter un partenaire</div>
            <input style={input} placeholder="Nom du restaurant" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} data-testid="partner-input-name" />
            <input style={input} placeholder="Email" value={pForm.email} onChange={(e) => setPForm({ ...pForm, email: e.target.value })} data-testid="partner-input-email" />
            <input style={input} placeholder="Téléphone" value={pForm.phone} onChange={(e) => setPForm({ ...pForm, phone: e.target.value })} data-testid="partner-input-phone" />
            <input style={input} placeholder="Adresse" value={pForm.address} onChange={(e) => setPForm({ ...pForm, address: e.target.value })} data-testid="partner-input-address" />
            <input style={input} placeholder="IBAN (optionnel)" value={pForm.iban} onChange={(e) => setPForm({ ...pForm, iban: e.target.value })} data-testid="partner-input-iban" />
            <input style={input} placeholder="Collection Shopify (optionnel)" value={pForm.shopify_collection} onChange={(e) => setPForm({ ...pForm, shopify_collection: e.target.value })} data-testid="partner-input-collection" />
            <button style={{ ...btn, marginTop: 10 }} onClick={submitPartner} data-testid="partner-submit">Créer le partenaire</button>
          </div>
        </div>
      )}

      {tab === "clients" && (
        <div data-testid="gestion-clients">
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input style={{ ...input, marginTop: 0 }} placeholder="Rechercher (email / nom / téléphone)" value={clientQ} onChange={(e) => setClientQ(e.target.value)} data-testid="client-search" />
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr><th style={th}>Email</th><th style={th}>Nom</th><th style={th}>Commandes</th><th style={th}>État</th><th style={th}></th></tr></thead>
            <tbody>
              {clients.length === 0 && <tr><td style={td} colSpan={5}>Aucun client.</td></tr>}
              {clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`}>
                  <td style={td}>{c.email}</td>
                  <td style={td}>{c.name || "—"}</td>
                  <td style={td}>{c.orders_count ?? 0}</td>
                  <td style={{ ...td, color: c.is_active ? "#7ee787" : "#ff7b72" }}>{c.is_active ? "Actif" : "Suspendu"}</td>
                  <td style={td}>
                    <button style={{ ...btn, padding: "4px 10px", background: c.is_active ? "#da3633" : "#238636", marginRight: 0 }} onClick={() => toggleClient(c)} data-testid={`client-toggle-${c.id}`}>
                      {c.is_active ? "Suspendre" : "Réactiver"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}
